import {
  BlockMapBuilder,
  CharacterMetadata,
  ContentBlock,
  Modifier,
  EditorState,
  SelectionState,
  genKey
} from 'draft-js'
import { List, Repeat, Map } from 'immutable'

export const hasText = editorState => editorState.getCurrentContent().hasText()

export const getSelectionAnchorPlacement = editorState => {
  const selectionState = editorState.getSelection()
  const anchorKey = selectionState.getAnchorKey()
  const contentState = editorState.getCurrentContent()
  
  let blockIndex = 0
  let keyBefore = contentState.getKeyBefore(anchorKey)
  while (keyBefore) {
    keyBefore = contentState.getKeyBefore(keyBefore)
    blockIndex++
  }

  return {
    blockIndex: blockIndex,
    offset: selectionState.getAnchorOffset()
  }
}

export const placeSelectionAnchor = (editorState, {blockIndex, offset}) => {
  const contentState = editorState.getCurrentContent()

  let key = contentState.getFirstBlock().getKey()
  for (let i = 0; i < blockIndex; i++) {
    key = contentState.getKeyAfter(key)
  }

  const selectionState = SelectionState.createEmpty(key);
  const updatedSelection = selectionState.merge({
    focusOffset: offset,
    anchorOffset: offset,
  })

  return EditorState.forceSelection(editorState, updatedSelection)
}

export const isEmptyListInEditor = (editorState) => {
  let placeholderBlockType
  const contentState = editorState.getCurrentContent()
  if (!contentState.hasText()) {
    placeholderBlockType = contentState.getBlockMap().first().getType()
  }

  return (
    placeholderBlockType === 'unordered-list-item' ||
    placeholderBlockType === 'ordered-list-item'
  )
}

export const embedAtomic = (editorState, data, entityType, mutablility='IMMUTABLE') => {
  const contentState = editorState.getCurrentContent()
  const contentStateWithEntity = contentState.createEntity(
    entityType,
    mutablility
  )
  
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
  const editorStateWithEntity = EditorState.set(
    editorState,
    {currentContent: contentStateWithEntity},
  )

  const dataMap = new Map(data)

  const newEditorState = insertAtomicBlockWithData(editorStateWithEntity, entityKey, dataMap, ' ')
  return newEditorState
}

// From: https://github.com/facebook/draft-js/issues/553#issuecomment-263298859
export const insertAtomicBlockWithData = (editorState, entityKey, blockData, character) => {
  const contentState = editorState.getCurrentContent()
  const selectionState = editorState.getSelection()

  const afterRemovalContentState = Modifier.removeRange(
    contentState,
    selectionState,
    'backward'
  )

  const targetSelectionState = afterRemovalContentState.getSelectionAfter()
  const afterSplitContentState = Modifier.splitBlock(afterRemovalContentState, targetSelectionState)
  const insertionTarget = afterSplitContentState.getSelectionAfter()

  const asAtomicBlock = Modifier.setBlockType(
    afterSplitContentState,
    insertionTarget,
    'atomic'
  )

  const charData = CharacterMetadata.create({ entity: entityKey })

  const fragmentArray = [
    new ContentBlock({
      key: genKey(),
      type: 'atomic',
      text: character,
      characterList: List(Repeat(charData, character.length)),
      data: blockData
    }),
    new ContentBlock({
      key: genKey(),
      type: 'unstyled',
      text: '',
      characterList: List()
    })
  ]

  const fragment = BlockMapBuilder.createFromArray(fragmentArray)

  const withAtomicBlock = Modifier.replaceWithFragment(
    asAtomicBlock,
    insertionTarget, fragment
  )

  const newContentState = withAtomicBlock.merge({
    selectionBefore: selectionState,
    selectionAfter: withAtomicBlock.getSelectionAfter().set('hasFocus', true)
  })

  return EditorState.push(editorState, newContentState, 'insert-fragment')
}
